import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import userContactService from '../services/userContactService';

const ITEMS_PER_PAGE = 15;

const UserContact = () => {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterType, setFilterType] = useState('all'); // 'all', 'processed', 'unprocessed'
  const [pagination, setPagination] = useState({
    lastEvaluatedKey: null,
    hasMore: false,
    count: 0,
  });
  const [pageTokens, setPageTokens] = useState({}); // Store tokens for each page
  const [updatingIds, setUpdatingIds] = useState(new Set()); // Track which items are being updated

  const loadData = async (page = 1, filter = 'all', token = null) => {
    setLoading(true);
    setError('');
    
    try {
      const filters = [];
      if (filter === 'processed') {
        filters.push({ key: 'isProcessed', value: true, operator: '=' });
      } else if (filter === 'unprocessed') {
        filters.push({ key: 'isProcessed', value: false, operator: '=' });
      }

      const options = {
        limit: ITEMS_PER_PAGE,
        exclusiveStartKey: token,
        filters: filters.length > 0 ? filters : undefined,
      };

      const response = await userContactService.getListData(options);
      
      // Handle both paginated response and simple array (backward compatibility)
      if (response && typeof response === 'object' && 'items' in response) {
        setItems(response.items || []);
        setPagination({
          lastEvaluatedKey: response.lastEvaluatedKey || null,
          hasMore: response.hasMore || false,
          count: response.count || 0,
        });
        // Store token for next page
        if (response.lastEvaluatedKey) {
          setPageTokens(prev => ({ ...prev, [page + 1]: response.lastEvaluatedKey }));
        }
      } else {
        // Backward compatibility: simple array response
        const itemArray = Array.isArray(response) ? response : [];
        setItems(itemArray);
        setPagination({
          lastEvaluatedKey: null,
          hasMore: false,
          count: itemArray.length,
        });
      }
    } catch (e) {
      console.error('Failed to load user contacts:', e);
      setError('載入用戶聯繫失敗');
      setItems([]);
      setPagination({ lastEvaluatedKey: null, hasMore: false, count: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    setPageTokens({});
    loadData(1, filterType);
  }, [filterType]);

  const handlePageChange = (newPage) => {
    if (newPage < 1) return;
    const token = pageTokens[newPage] || null;
    setCurrentPage(newPage);
    loadData(newPage, filterType, token);
  };

  const handleFilterChange = (newFilter) => {
    setFilterType(newFilter);
  };

  const handleMarkAsProcessed = async (contactId) => {
    if (!contactId) return;
    
    setUpdatingIds(prev => new Set(prev).add(contactId));
    
    try {
      await userContactService.update(contactId, { isProcessed: true });
      
      // Update local state immediately for better UX
      setItems(prevItems => 
        prevItems.map(item => 
          item.id === contactId 
            ? { ...item, isProcessed: true }
            : item
        )
      );
      
      // If filtering by unprocessed, remove the item from the list
      if (filterType === 'unprocessed') {
        setItems(prevItems => prevItems.filter(item => item.id !== contactId));
      }
    } catch (e) {
      console.error('Failed to mark contact as processed:', e);
      setError('更新聯繫狀態失敗');
    } finally {
      setUpdatingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(contactId);
        return newSet;
      });
    }
  };

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const totalPages = pagination.hasMore ? currentPage + 1 : currentPage;

  return (
    <section className="py-5">
      <div className="container">
        <h1 className="mb-4">用戶聯繫</h1>
        
        {/* Filter Radio Buttons */}
        <div className="mb-4">
          <div className="btn-group" role="group" aria-label="篩選聯繫">
            <input
              type="radio"
              className="btn-check"
              name="filterType"
              id="filterAll"
              checked={filterType === 'all'}
              onChange={() => handleFilterChange('all')}
            />
            <label className="btn btn-outline-primary" htmlFor="filterAll">
              全部數據
            </label>

            <input
              type="radio"
              className="btn-check"
              name="filterType"
              id="filterProcessed"
              checked={filterType === 'processed'}
              onChange={() => handleFilterChange('processed')}
            />
            <label className="btn btn-outline-primary" htmlFor="filterProcessed">
              已處理
            </label>

            <input
              type="radio"
              className="btn-check"
              name="filterType"
              id="filterUnprocessed"
              checked={filterType === 'unprocessed'}
              onChange={() => handleFilterChange('unprocessed')}
            />
            <label className="btn btn-outline-primary" htmlFor="filterUnprocessed">
              未處理
            </label>
          </div>
        </div>

        {loading && <div className="text-muted">載入中...</div>}
        {error && <div className="alert alert-danger">{error}</div>}
        
        {!loading && !error && (
          <>
            <div className="table-responsive">
              <table className="table table-striped table-hover align-middle">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>姓名</th>
                    <th>電子郵件</th>
                    <th>電話</th>
                    <th>信息</th>
                    <th>已處理</th>
                    <th>創建時間</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center text-muted">
                        未找到聯繫提交。
                      </td>
                    </tr>
                  )}
                  {items.map((it) => (
                    <tr key={it.id}>
                      <td>{it.id}</td>
                      <td>{it.userName || it.name}</td>
                      <td>{it.email}</td>
                      <td>{it.phone}</td>
                      <td style={{ whiteSpace: 'pre-wrap', maxWidth: '300px' }}>
                        {it.information || it.message}
                      </td>
                      <td>
                        {it.isProcessed ? (
                          <span className="badge bg-success">是</span>
                        ) : (
                          <span className="badge bg-warning">否</span>
                        )}
                      </td>
                      <td>{it.createdAt}</td>
                      <td>
                        {it.isProcessed ? (
                          <span className="text-muted small">已處理</span>
                        ) : (
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleMarkAsProcessed(it.id)}
                            disabled={updatingIds.has(it.id)}
                          >
                            {updatingIds.has(it.id) ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                處理中...
                              </>
                            ) : (
                              '標記為已處理'
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {items.length > 0 && (
              <nav aria-label="頁面導航" className="mt-4">
                <ul className="pagination justify-content-center">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      上一頁
                    </button>
                  </li>
                  
                  {currentPage > 1 && (
                    <li className="page-item">
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        {currentPage - 1}
                      </button>
                    </li>
                  )}
                  
                  <li className="page-item active">
                    <span className="page-link">{currentPage}</span>
                  </li>
                  
                  {pagination.hasMore && (
                    <li className="page-item">
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        {currentPage + 1}
                      </button>
                    </li>
                  )}
                  
                  <li className={`page-item ${!pagination.hasMore ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!pagination.hasMore}
                    >
                      下一頁
                    </button>
                  </li>
                </ul>
                <div className="text-center text-muted mt-2">
                  第 {currentPage} 頁顯示 {items.length} 項
                  {pagination.hasMore && ' （還有更多）'}
                </div>
              </nav>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default UserContact;
